import { type FC, type KeyboardEvent, type MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RenderMarkdown } from 'MarkdownComponents';
import { Text } from 'Text';
import { Tooltip } from 'Tooltip';
import { ClickableText } from 'ClickableText';
import summaryIcon from 'vectors/summary.svg';
import { type LessonForStreamDashboard } from 'Lessons';
import { type StreamSummaryLink } from '../StreamDashboard.types';
import { LessonProgressColumn } from './LessonProgressColumn';
import '../locales/streamDashboard/streamDashboard-en.json';

/*
    One lesson in an expanded chapter. The row draws no divider: ChapterList adds one, so it does not run under
    the pinned launch button.
    The closest('button') guard stops a launch-button click from launching twice. jsdom cannot prove the guard,
    because react-aria takes a different path there.
*/

type Props = {
    lesson: LessonForStreamDashboard;
    lessonNumber: number;
    isComingSoon: boolean;
    isSignedIn: boolean;
    isInProgress: boolean;
    isComplete: boolean;
    canLaunch: boolean;
    isClickable: boolean;
    showAssessmentArc: boolean;
    showMobileScore: boolean;
    formattedBestScore: string;
    launchTextKey: string;
    summaries: StreamSummaryLink[];
    summariesDisabled: boolean;
    onLaunch: () => void;
    onRowClick: () => void;

    // Null when the lesson is launchable or on a phone. Desktop shows the reason as a tooltip on this row.
    lockedTooltipMessage: string | null;
    onSummaryClick: (summaryId: string) => void;
};

/*
    A "+" marker rather than a bullet, which the legacy rule drew with `li::after { content: "+" }` and a
    15px hanging indent. It is a span here so that screen readers skip it, and !list-none beats
    `.stream-dashboard ul` in oreo/global/tailwind.scss, which restores browser bullets on Angular routes.
*/
const LessonDescriptionList: FC<{ description: string[] }> = ({ description }) => {
    /*
        A lesson with no description still reserves the space, which is what
        `.lesson-description { min-height: 2em }` did: 30px at the description's 15px. Without it the row
        collapses to the height of its title, and the progress badge beside it has no room to breathe.

        Mobile is unaffected. The description is hidden below sm, and the progress column there is a 30px
        bar rather than a badge.
    */
    if (!description.length) return <div className="min-h-7.5 max-sm:hidden" />;

    return (
        <ul className="mb-2.5 !list-none p-0 !ps-0 text-sm leading-5 text-black max-sm:hidden">
            {description.map(descriptionLine => (
                <li key={descriptionLine} className="flex gap-2.5">
                    <span aria-hidden className="w-2.5 shrink-0">
                        +
                    </span>
                    <RenderMarkdown>{descriptionLine}</RenderMarkdown>
                </li>
            ))}
        </ul>
    );
};

// The dividers are decorative, so screen readers skip them.
const LessonIndexRow: FC<{
    lesson: LessonForStreamDashboard;
    lessonNumber: number;
    isComingSoon: boolean;
}> = ({ lesson, lessonNumber, isComingSoon }) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'streamDashboard.streamDashboard' });

    return (
        <div className="mb-1 flex flex-wrap items-center gap-x-1 text-sm font-semibold text-slate-grey-500">
            <span>{t('lesson_number_x', { number: lessonNumber })}</span>
            {(lesson.assessment || lesson.test) && (
                <>
                    <span aria-hidden>|</span>
                    <span>{t(lesson.assessment ? 'smartcase' : 'test')}</span>
                </>
            )}
            {!lesson.test && isComingSoon && (
                <>
                    <span aria-hidden>|</span>
                    <span>{t('coming_soon_inline')}</span>
                </>
            )}
        </div>
    );
};

/*
    Everything to the left of the progress column: the index line, the title, the mobile score and the
    summary links. Extracted from LessonRow to keep that function inside the 100-line lint cap.
*/
const LessonRowDetails: FC<{
    lesson: LessonForStreamDashboard;
    lessonNumber: number;
    isComingSoon: boolean;
    isAssessmentOrTest: boolean;
    showMobileScore: boolean;
    formattedBestScore: string;
    summaries: StreamSummaryLink[];
    summariesDisabled: boolean;
    onSummaryClick: (summaryId: string) => void;
}> = ({
    lesson,
    lessonNumber,
    isComingSoon,
    isAssessmentOrTest,
    showMobileScore,
    formattedBestScore,
    summaries,
    summariesDisabled,
    onSummaryClick,
}) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'streamDashboard.streamDashboard' });

    return (
        // 20px all round, which is what the legacy .lesson-description-box carried. At 14px every
        // row came out 11px shorter than main's, and the badge beside a short row had no room.
        <div className="flex-1 p-5">
            <LessonIndexRow lesson={lesson} lessonNumber={lessonNumber} isComingSoon={isComingSoon} />

            {/* An exam or a SMARTcase reads turquoise-dark rather than black. The legacy rules gave
                .lesson-details-box.test and .assessment the same #106E6E, so both share it here. */}
            <Text
                as="p"
                weight="semibold"
                uppercase
                className={`mb-5 text-md leading-[1.05] sm:text-lg ${isAssessmentOrTest ? 'text-turquoise-dark' : 'text-black'}`}
            >
                {lesson.title}
            </Text>

            {showMobileScore && (
                <Text as="p" className="text-beige-darkest sm:hidden">
                    {t('completed_score', { score: formattedBestScore })}
                </Text>
            )}

            {!isComingSoon && <LessonDescriptionList description={lesson.description} />}

            {!isComingSoon &&
                summaries.map(summary => (
                    <ClickableText
                        key={summary.id}
                        onPress={() => onSummaryClick(summary.id)}
                        disabled={summariesDisabled}
                        color="primary"
                        underline="none"
                        weight="semibold"
                        allowWrap
                        className="flex items-center gap-2.5 text-start text-sm"
                    >
                        {/* 25px, which is what the legacy summary icon measured. */}
                        <img src={summaryIcon} alt="" width="25" height="25" data-no-retina />
                        {summary.title}
                    </ClickableText>
                ))}
        </div>
    );
};

export const LessonRow: FC<Props> = ({
    lesson,
    lessonNumber,
    isComingSoon,
    isSignedIn,
    isInProgress,
    isComplete,
    canLaunch,
    isClickable,
    showAssessmentArc,
    showMobileScore,
    formattedBestScore,
    launchTextKey,
    summaries,
    summariesDisabled,
    onLaunch,
    onRowClick,
    lockedTooltipMessage,
    onSummaryClick,
}) => {
    const isAssessmentOrTest = lesson.assessment || lesson.test;

    /*
        The legacy tooltip used tooltip-trigger="'outsideClick'", so it opened on click rather than on
        hover and stayed up until the next click elsewhere. HeroUI hovers by default, so the open
        state is controlled here to keep the old interaction.
    */
    const [lockedTooltipOpen, setLockedTooltipOpen] = useState(false);
    const showLockedTooltip = !!lockedTooltipMessage;

    const activateRow = () => {
        if (showLockedTooltip) setLockedTooltipOpen(true);
        onRowClick();
    };

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
        if ((event.target as HTMLElement).closest('button')) return;
        activateRow();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        activateRow();
    };

    return (
        <Tooltip
            content={lockedTooltipMessage}
            isDisabled={!showLockedTooltip}
            isOpen={lockedTooltipOpen}
            onOpenChange={setLockedTooltipOpen}
            multiline
        >
            <div
                role="button"
                /*
                    Focusable even when locked. aria-disabled marks the row unavailable without
                    removing it from the tab order, and a locked row still has something to say: on
                    desktop the reason lives in a tooltip that only activation opens.
                */
                tabIndex={0}
                aria-disabled={!isClickable}
                className={`flex ${isClickable ? 'cursor-pointer' : ''}`}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
            >
                <LessonRowDetails
                    lesson={lesson}
                    lessonNumber={lessonNumber}
                    isComingSoon={isComingSoon}
                    isAssessmentOrTest={isAssessmentOrTest}
                    showMobileScore={showMobileScore}
                    formattedBestScore={formattedBestScore}
                    summaries={summaries}
                    summariesDisabled={summariesDisabled}
                    onSummaryClick={onSummaryClick}
                />

                <LessonProgressColumn
                    isComingSoon={isComingSoon}
                    isSignedIn={isSignedIn}
                    isAssessment={lesson.assessment}
                    isAssessmentOrTest={isAssessmentOrTest}
                    isInProgress={isInProgress}
                    isComplete={isComplete}
                    canLaunch={canLaunch}
                    isClickable={isClickable}
                    showAssessmentArc={showAssessmentArc}
                    bestScore={lesson.lessonProgress?.bestScore ?? undefined}
                    launchTextKey={launchTextKey}
                    onLaunch={onLaunch}
                />
            </div>
        </Tooltip>
    );
};
